import { useEffect, useState } from 'react';
import { ActivityIndicator, Pressable, StyleSheet } from 'react-native';
import { Box } from '../../../components/primitives/Box';
import { Button } from '../../../components/primitives/Button';
import { DatePickerInput } from '../../../components/primitives/DatePickerInput';
import { Divider } from '../../../components/primitives/Divider';
import { HStack, VStack } from '../../../components/primitives/Stack';
import { Text } from '../../../components/primitives/Text';
import { TimePickerInput } from '../../../components/primitives/TimePickerInput';
import { Toggle } from '../../../components/primitives/Toggle';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from '../../../contexts/TranslationContext';
import { useCurrentOrgRole } from '../../../hooks/useAuthOrgRole';
import { useOrgSettings, useUpdateOrgSettings } from '../../../hooks/useOrgSettings';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import {
  BUSINESS_DAYS,
  type BusinessDayKey,
  type BusinessHours,
  type BusinessHoursFormState,
  buildBusinessHours,
  findWindowProblem,
  toFormState,
} from '../utils/businessHours';
import { SettingsSection } from './SettingsSection';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

// The days and hours the team can meet. The voice agent, the agents platform
// and chat only offer and book inside these windows once they are enabled
// (FLOSEAI-690); off, nothing changes for the tenant.
export function BusinessHoursSection() {
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.id;
  const { t } = useTranslation();
  const role = useCurrentOrgRole();
  const canManage = role === 'owner' || role === 'admin';

  const { data: settings, isLoading } = useOrgSettings(orgId);
  const updateSettings = useUpdateOrgSettings();

  const stored = settings?.settingsOverrides?.businessHours as BusinessHours | undefined;
  const [form, setForm] = useState<BusinessHoursFormState>(() => toFormState(stored));
  const [blackoutDraft, setBlackoutDraft] = useState('');

  useEffect(() => {
    setForm(toFormState(stored));
  }, [stored]);

  const problem = findWindowProblem(form);
  const timezone = currentOrganization?.timezone || t('settings.businessHours.timezoneNotSet');

  const toggleDay = (day: BusinessDayKey) =>
    setForm((prev) => ({
      ...prev,
      days: prev.days.includes(day) ? prev.days.filter((d) => d !== day) : [...prev.days, day],
    }));

  const addBlackout = () => {
    const value = blackoutDraft.trim();
    if (!ISO_DATE.test(value) || form.blackoutDates.includes(value)) return;
    setForm((prev) => ({ ...prev, blackoutDates: [...prev.blackoutDates, value] }));
    setBlackoutDraft('');
  };

  const handleSave = () => {
    if (!orgId || problem) return;
    updateSettings.mutate({
      orgId,
      businessHours: buildBusinessHours(form, currentOrganization?.timezone ?? null),
    });
  };

  if (isLoading || role === undefined) {
    return (
      <Box
        backgroundColor={colors.background.card}
        borderRadius="xl"
        borderWidth={1}
        borderColor={colors.border.subtle}
        padding="xl"
        alignItems="center"
        justifyContent="center"
        minHeight={200}
      >
        <ActivityIndicator size="large" color={colors.primary[500]} />
      </Box>
    );
  }

  return (
    <SettingsSection title={t('settings.businessHours.title')}>
      <Text variant="body" color={colors.text.secondary}>
        {t('settings.businessHours.description')}
      </Text>

      <Divider color={colors.border.muted} />

      <HStack spacing="md" align="center" justify="between">
        <VStack spacing="xxs" style={{ flex: 1 }}>
          <Text variant="label" color={colors.text.primary}>
            {t('settings.businessHours.enabledLabel')}
          </Text>
          <Text variant="caption" color={colors.text.tertiary}>
            {form.enabled
              ? t('settings.businessHours.enabledHint')
              : t('settings.businessHours.disabledHint')}
          </Text>
        </VStack>
        {canManage ? (
          <Toggle
            testID="business-hours-enabled"
            value={form.enabled}
            onValueChange={(enabled) => setForm((prev) => ({ ...prev, enabled }))}
            accessibilityLabel={t('settings.businessHours.enabledLabel')}
          />
        ) : (
          <Text variant="body" color={colors.text.secondary}>
            {form.enabled ? t('common.actions.on') : t('common.actions.off')}
          </Text>
        )}
      </HStack>

      <VStack spacing="sm">
        <Text variant="label" color={colors.text.secondary}>
          {t('settings.businessHours.daysLabel')}
        </Text>
        <HStack style={{ flexWrap: 'wrap', gap: tokens.spacing.sm }}>
          {BUSINESS_DAYS.map(({ key, labelKey }) => {
            const selected = form.days.includes(key);
            return (
              <Pressable
                key={key}
                testID={`business-hours-day-${key}`}
                accessibilityRole="checkbox"
                accessibilityState={{ checked: selected, disabled: !canManage }}
                accessibilityLabel={t(labelKey)}
                disabled={!canManage}
                onPress={() => toggleDay(key)}
              >
                <Box
                  backgroundColor={selected ? colors.primary.subtle : colors.background.input}
                  borderRadius="md"
                  borderWidth={1}
                  borderColor={selected ? colors.primary.main : colors.border.input}
                  style={styles.dayChip}
                >
                  <Text
                    variant="caption"
                    color={selected ? colors.primary.main : colors.text.tertiary}
                    style={{ fontWeight: selected ? '600' : '400' }}
                  >
                    {t(labelKey)}
                  </Text>
                </Box>
              </Pressable>
            );
          })}
        </HStack>
      </VStack>

      <HStack spacing="lg">
        <VStack spacing="xs" style={{ flex: 1 }}>
          <Text variant="label" color={colors.text.secondary}>
            {t('settings.businessHours.startLabel')}
          </Text>
          {canManage ? (
            <TimePickerInput
              testID="business-hours-start"
              value={form.start}
              onChange={(start) => setForm((prev) => ({ ...prev, start }))}
              accessibilityLabel={t('settings.businessHours.startLabel')}
            />
          ) : (
            <Text variant="body" color={colors.text.secondary}>
              {form.start}
            </Text>
          )}
        </VStack>
        <VStack spacing="xs" style={{ flex: 1 }}>
          <Text variant="label" color={colors.text.secondary}>
            {t('settings.businessHours.endLabel')}
          </Text>
          {canManage ? (
            <TimePickerInput
              testID="business-hours-end"
              value={form.end}
              onChange={(end) => setForm((prev) => ({ ...prev, end }))}
              accessibilityLabel={t('settings.businessHours.endLabel')}
            />
          ) : (
            <Text variant="body" color={colors.text.secondary}>
              {form.end}
            </Text>
          )}
        </VStack>
      </HStack>

      <Text variant="caption" color={colors.text.tertiary} testID="business-hours-timezone">
        {t('settings.businessHours.timezoneHint')} {timezone}
      </Text>
      {!form.isUniform && (
        <Box
          backgroundColor={colors.status.warning.bg}
          borderRadius="md"
          padding="md"
          testID="business-hours-per-day-notice"
        >
          <Text variant="bodySmall" color={colors.status.warning.main}>
            {t('settings.businessHours.perDayNotice')}
          </Text>
        </Box>
      )}

      <VStack spacing="sm">
        <Text variant="label" color={colors.text.secondary}>
          {t('settings.businessHours.blackoutLabel')}
        </Text>
        <Text variant="caption" color={colors.text.tertiary}>
          {t('settings.businessHours.blackoutHint')}
        </Text>
        {canManage && (
          <HStack spacing="sm" align="center">
            <Box style={{ flex: 1 }}>
              <DatePickerInput
                testID="business-hours-blackout-input"
                value={blackoutDraft}
                onChange={setBlackoutDraft}
                accessibilityLabel={t('settings.businessHours.blackoutLabel')}
              />
            </Box>
            <Button
              variant="secondary"
              size="sm"
              onPress={addBlackout}
              disabled={!ISO_DATE.test(blackoutDraft.trim())}
              testID="business-hours-blackout-add"
            >
              {t('common.actions.add')}
            </Button>
          </HStack>
        )}
        <HStack style={{ flexWrap: 'wrap', gap: tokens.spacing.sm }}>
          {form.blackoutDates.map((date) => (
            <HStack
              key={date}
              spacing="xs"
              align="center"
              testID={`business-hours-blackout-${date}`}
              style={styles.blackoutChip}
            >
              <Text variant="caption" color={colors.text.primary}>
                {date}
              </Text>
              {canManage && (
                <Pressable
                  testID={`business-hours-blackout-remove-${date}`}
                  accessibilityRole="button"
                  accessibilityLabel={`${t('common.actions.remove')} ${date}`}
                  hitSlop={8}
                  onPress={() =>
                    setForm((prev) => ({
                      ...prev,
                      blackoutDates: prev.blackoutDates.filter((d) => d !== date),
                    }))
                  }
                >
                  <Text variant="caption" color={colors.text.tertiary}>
                    ×
                  </Text>
                </Pressable>
              )}
            </HStack>
          ))}
        </HStack>
      </VStack>

      {problem && (
        <Text variant="caption" color={colors.status.error.main} accessibilityRole="alert">
          {t(`settings.businessHours.errors.${problem}`)}
        </Text>
      )}
      {updateSettings.isSuccess && (
        <Text variant="caption" color={colors.status.success.main}>
          {t('settings.businessHours.saveSuccess')}
        </Text>
      )}
      {updateSettings.isError && (
        <Text variant="caption" color={colors.status.error.main}>
          {t('settings.businessHours.saveError')}
        </Text>
      )}

      {canManage && (
        <HStack spacing="sm" justify="end">
          <Button
            variant="primary"
            size="md"
            onPress={handleSave}
            loading={updateSettings.isPending}
            disabled={updateSettings.isPending || problem !== null}
            testID="business-hours-save"
          >
            {t('common.actions.saveChanges')}
          </Button>
        </HStack>
      )}
    </SettingsSection>
  );
}

const styles = StyleSheet.create({
  dayChip: {
    width: 44,
    height: 38,
    alignItems: 'center',
    justifyContent: 'center',
  },
  blackoutChip: {
    backgroundColor: colors.background.input,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: colors.border.input,
    paddingHorizontal: tokens.spacing.sm,
    paddingVertical: tokens.spacing.xs,
  },
});
